import * as React from 'react';
import { FC } from 'react';

import { makeUclComponent } from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import LoadingAnimation from 'components/loading-animation';
import { LogUserErrorSeverity, useGetAndLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useShortCodeRefresh } from 'hooks/loyalty/use-short-code-refresh';
import {
  ErrorMessage,
  ErrorMessageTitle,
  ShortCodeContainer,
  ShortCodeContent,
  ShortCodeErrorContainer,
  ShortCodeRefreshBtnContainer,
  ShortCodeRefreshButton,
  ShortCodeSubtitleRedesign,
  ShortCodeText,
  ShortCodeTitleRedesign,
  ShortCodeWidgetContainerRedesign,
  StyledRefreshIcon,
  StyledShortCodeErrorActionButton,
} from 'pages/loyalty/loyalty-in-restaurant-redemption/loyalty-in-restaurant-redemption.styled';
import { IRefreshCodeProps } from 'pages/loyalty/loyalty-in-restaurant-redemption/types';
import { useLoyaltyMyCode } from 'pages/loyalty/loyalty-in-restaurant-redemption/use-loyalty-my-code';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  ShortCodeState,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { addStringInBetween } from 'utils/add-string-in-between';
import { TraceName, stopTrace } from 'utils/performance/trace';

const ShortCodeErrorBox: FC<React.PropsWithChildren<IRefreshCodeProps>> = ({
  onRefreshPress,
  isCodeLoading,
  newUI,
  children,
}) => {
  useShortCodeRefresh();

  return (
    <ShortCodeErrorContainer testID="short-code-error-box" newUI>
      {children}
      <StyledShortCodeErrorActionButton
        isLoading={isCodeLoading}
        onPress={onRefreshPress}
        startIcon={<StyledRefreshIcon />}
        newUI={newUI}
      >
        {t('refresh')}
      </StyledShortCodeErrorActionButton>
    </ShortCodeErrorContainer>
  );
};

const LoyaltyShortCodeWidgetRedesign: React.FC = () => {
  const {
    shortCodeState,
    shortCodeLoading,
    shortCode,
    generateShortCode,
  } = useInRestaurantRedemptionContext();
  const { loading, data } = useLoyaltyMyCode();
  const enableQrCodeLoyaltyIdFlag = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_QR_CODE_LOYALTY_ID);

  const shortCodeErrorRefresh = t('pleaseTryToRefresh');
  const getShortCodeErrorMessage = useGetAndLogUserErrorMessage({
    message: t('codeErrorMessage'),
    severity: LogUserErrorSeverity.Recoverable,
  });
  const getRefreshMessage = useGetAndLogUserErrorMessage({
    message: t('refresh'),
    severity: LogUserErrorSeverity.Recoverable,
    context: { moreInfo: 'short-code' },
  });

  const shortCodeTitle = enableQrCodeLoyaltyIdFlag
    ? data?.myCodeOtpHeading?.locale
    : data?.myCodeOtpHeadingQrDisabled?.locale;
  const shortCodeSubTitle = enableQrCodeLoyaltyIdFlag
    ? data?.myCodeOtpSubheading?.locale
    : data?.myCodeOtpSubheadingQrDisabled?.locale;
  const shortCodeText = addStringInBetween(shortCode || '', 2, '  ');

  return (
    <ShortCodeWidgetContainerRedesign>
      {!loading && (
        <ShortCodeContainer>
          <ShortCodeTitleRedesign content={shortCodeTitle} />
          <View style={styles.subtitleAndCodeOuter}>
            <View style={styles.subtitleAndCodeInner}>
              <ShortCodeSubtitleRedesign content={shortCodeSubTitle} />
              {([ShortCodeState.Pending, ShortCodeState.Claimed].includes(shortCodeState) ||
                (shortCodeState === ShortCodeState.None && shortCodeLoading)) && (
                <ShortCodeContent testID="short-code" newUI={false}>
                  {shortCodeLoading ? (
                    <LoadingAnimation />
                  ) : (
                    <ShortCodeText
                      testID="short-code-text"
                      newUI={false}
                      accessibilityRole="alert"
                      accessibilityLabel={shortCodeText}
                      onLayout={() => stopTrace(TraceName.MY_CODE_LOAD)}
                    >
                      {shortCodeText}
                    </ShortCodeText>
                  )}
                </ShortCodeContent>
              )}
              {shortCodeState === ShortCodeState.Expired && (
                <ShortCodeRefreshBtnContainer>
                  <ShortCodeRefreshButton
                    isLoading={shortCodeLoading}
                    disabled={shortCodeLoading}
                    onPress={generateShortCode}
                    startIcon={<StyledRefreshIcon />}
                  >
                    {getRefreshMessage()}
                  </ShortCodeRefreshButton>
                </ShortCodeRefreshBtnContainer>
              )}
              {shortCodeState === ShortCodeState.RequestError && (
                <ShortCodeErrorBox
                  onRefreshPress={generateShortCode}
                  isCodeLoading={shortCodeLoading}
                >
                  <ErrorMessageTitle
                    accessible
                    accessibilityRole="alert"
                    accessibilityLabel={getShortCodeErrorMessage()}
                  >
                    {getShortCodeErrorMessage()}
                  </ErrorMessageTitle>
                  <ErrorMessage
                    accessible
                    accessibilityRole="alert"
                    accessibilityLabel={shortCodeErrorRefresh}
                  >
                    {shortCodeErrorRefresh}
                  </ErrorMessage>
                </ShortCodeErrorBox>
              )}
            </View>
          </View>
        </ShortCodeContainer>
      )}
    </ShortCodeWidgetContainerRedesign>
  );
};

const styles = StyleSheet.create({
  subtitleAndCodeOuter: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  subtitleAndCodeInner: {
    flexDirection: 'column',
  },
});

export default makeUclComponent(LoyaltyShortCodeWidgetRedesign);
